import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Snackbar,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  MoreVertical,
  UserPlus,
  X,
  CheckCircle2,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  Lock,
  Unlock,
  Edit2,
  Users as UsersIcon,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, addUser, updateUserStatus, deleteUser, updateUserDetails } from '../redux/slices/userSlice';
import { formatDateTime } from '../utils/formatDate';
import { validatePhoneNumber } from '../utils/phoneValidator';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import {
  AdminDialog,
  AdminDialogHeader,
  AdminDialogContent,
  AdminDialogActions,
  DeleteConfirmDialog,
  FilterDrawer,
} from '../components/common/modal';
import axios from '../utils/axios';
import ExportTableButton from '../components/common/ExportTableButton';

// Deterministic avatar background from a string so each user keeps a stable,
// theme-agnostic colour (readable in both light & dark with white text).
const avatarColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 45%, 45%)`;
};

const UsersView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { users, totalUsers, isLoading, error } = useSelector((state) => state.user);


  // States for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // States for filters
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isVerifiedFilter, setIsVerifiedFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const activeFiltersCount = [roleFilter, statusFilter, isVerifiedFilter].filter(Boolean).length;
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // States for Add User Dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'user',
    userType: 'user',
    password: 'Password@123' // Default password for new users
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // States for Edit User Dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editUser, setEditUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'user',
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadUsers = useCallback(() => {
    dispatch(fetchUsers({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      role: roleFilter,
      status: statusFilter,
      isVerified: isVerifiedFilter
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch, roleFilter, statusFilter, isVerifiedFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenFilter = () => {
    setIsFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleOpenActionMenu = (event, user) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleUpdateStatus = async (type, value) => {
    if (!selectedUser) return;

    try {
      const data = type === 'status' ? { auth_status: value } : { isVerified: value };
      const resultAction = await dispatch(updateUserStatus({ id: selectedUser._id, data }));

      if (updateUserStatus.fulfilled.match(resultAction)) {
        setSnackbar({
          open: true,
          message: `User ${type === 'status' ? 'status' : 'verification'} updated successfully!`,
          severity: 'success'
        });
        loadUsers();
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Update failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    }
    handleCloseActionMenu();
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    try {
      const resultAction = await dispatch(deleteUser(selectedUser._id));
      if (deleteUser.fulfilled.match(resultAction)) {
        setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
        loadUsers();
        setOpenDeleteDialog(false);
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An error occurred', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setActionMenuAnchor(null);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };


  const handleResetFilters = () => {
    setRoleFilter('');
    setStatusFilter('');
    setIsVerifiedFilter('');
    setSearchTerm('');
    setPage(0);
    handleCloseFilter();
  };

  const handleOpenAddDialog = () => setOpenAddDialog(true);
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewUser({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'user',
      userType: 'user',
      password: 'Password@123'
    });
  };

  const handeAddUserChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = (name !== 'role' && name !== 'userType') ? value.trimStart() : value;
    setNewUser(prev => ({
      ...prev,
      [name]: trimmedValue,
      ...(name === 'role' ? { userType: value } : {})
    }));
  };

  const handleAddUserSubmit = async () => {
    if (!newUser.first_name || !newUser.last_name || !newUser.email || !newUser.phone) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    const phoneCheck = validatePhoneNumber(newUser.phone);
    if (!phoneCheck.isValid) {
      setSnackbar({ open: true, message: phoneCheck.error || 'Please enter a valid, authentic mobile number.', severity: 'error' });
      return;
    }

    setIsSubmitting(true);
    // Trim all string fields before submitting
    const trimmedUser = {
      ...newUser,
      first_name: newUser.first_name.trim(),
      last_name: newUser.last_name.trim(),
      email: newUser.email.trim(),
      phone: phoneCheck.cleanPhone,
    };
    try {
      const resultAction = await dispatch(addUser(trimmedUser));
      if (addUser.fulfilled.match(resultAction)) {
        setSnackbar({ open: true, message: 'User added successfully! OTP sent to email.', severity: 'success' });
        handleCloseAddDialog();
        loadUsers();
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Failed to add user', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditDialog = () => {
    if (selectedUser) {
      setEditUser({
        first_name: selectedUser.first_name || '',
        last_name: selectedUser.last_name || '',
        email: selectedUser.email || '',
        phone: selectedUser.phone || '',
        role: selectedUser.role || selectedUser.userType || 'user',
      });
      setOpenEditDialog(true);
      setActionMenuAnchor(null);
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = (name !== 'role') ? value.trimStart() : value;
    setEditUser(prev => ({
      ...prev,
      [name]: trimmedValue,
    }));
  };

  const handleEditUserSubmit = async () => {
    if (!editUser.first_name || !editUser.last_name || !editUser.email || !editUser.phone) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    const phoneCheck = validatePhoneNumber(editUser.phone);
    if (!phoneCheck.isValid) {
      setSnackbar({ open: true, message: phoneCheck.error || 'Please enter a valid, authentic mobile number.', severity: 'error' });
      return;
    }

    setIsSubmitting(true);
    const trimmedUser = {
      ...editUser,
      first_name: editUser.first_name.trim(),
      last_name: editUser.last_name.trim(),
      email: editUser.email.trim(),
      phone: phoneCheck.cleanPhone,
    };
    try {
      const resultAction = await dispatch(updateUserDetails({ id: selectedUser._id, data: trimmedUser }));
      if (updateUserDetails.fulfilled.match(resultAction)) {
        setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
        handleCloseEditDialog();
        loadUsers();
      } else {
        setSnackbar({ open: true, message: resultAction.payload || 'Failed to update user', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage customer accounts, administrative roles, and verification statuses"
        count={totalUsers}
        countLabel="users"
        action={
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<UserPlus size={18} />}
            onClick={handleOpenAddDialog}
          >
            New user
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search by name, email or phone..."
          onOpenFilter={handleOpenFilter}
          filterCount={activeFiltersCount}
          hasActiveFilters={Boolean(roleFilter || statusFilter || isVerifiedFilter || searchTerm)}
          onResetFilters={handleResetFilters}
        >
          <ExportTableButton
            data={users}
            total={totalUsers}
            onExportAll={async () => {
              const res = await axios.get('/admin/users', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                  role: roleFilter || undefined,
                  status: statusFilter || undefined,
                  isVerified: isVerifiedFilter || undefined,
                },
              });
              return res.data.users || [];
            }}
            columns={[
              { label: 'First Name', key: 'first_name' },
              { label: 'Last Name', key: 'last_name' },
              { label: 'Email', key: 'email' },
              { label: 'Phone', key: 'phone' },
              { label: 'OTP', key: 'otp' },
              { label: 'Role', value: (row) => row.role || row.userType || '' },
              { label: 'User Type', key: 'userType' },
              { label: 'Verified', value: (row) => row.isVerified ? 'Yes' : 'No' },
              { label: 'Status', value: (row) => row.auth_status || row.status || '' },
              { label: 'Created', key: 'createdAt' },
            ]}
            filename="users"
          />
        </FilterBar>

        {error ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>

              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Contact</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>OTP</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Created</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {users && users.length > 0 ? users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: avatarColor(`${user.first_name || ''} ${user.last_name || ''}${user._id || ''}`), color: '#fff', fontSize: 15, fontWeight: 700 }}
                        >
                          {`${(user.first_name || '').charAt(0)}${(user.last_name || '').charAt(0)}`.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{user.first_name} {user.last_name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {user.isVerified ? (
                              <CheckCircle2 size={12} color={theme.palette.success.main} />
                            ) : (
                              <X size={12} color={theme.palette.error.main} />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {user.isVerified ? 'Verified' : 'Unverified'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {user.otp || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                        {user.role || user.userType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.userType ? (
                        <Chip
                          label={user.userType}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 700,
                            borderColor: (theme) => alpha(theme.palette.info.main, 0.4),
                            color: 'info.dark',
                            backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                            borderRadius: 1
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={user.auth_status === 'Blocked' ? 'inactive' : 'active'}
                        label={user.auth_status || 'Activated'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>{formatDateTime(user.createdAt || user.created_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton aria-label="Row actions" size="small" onClick={(e) => handleOpenActionMenu(e, user)}>
                        <MoreVertical size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )) : !isLoading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ p: 0 }}>
                      <EmptyState
                        icon={UsersIcon}
                        title="No users found"
                        description="Try adjusting your filters or search query to find customer accounts."
                        actionLabel="Add User"
                        onAction={handleOpenAddDialog}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={totalUsers || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block'
              }
            }}
          />
        </Box>

      </Card>

      {/* Add New User Dialog */}
      <AdminDialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <AdminDialogHeader
          title="Add New User"
          subtitle="Create a new administrative or platform user with email verification"
          icon={UserPlus}
          iconColor="primary"
          onClose={handleCloseAddDialog}
        />
        <AdminDialogContent dividers>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={newUser.first_name}
                onChange={handeAddUserChange}
                required
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={newUser.last_name}
                onChange={handeAddUserChange}
                required
                variant="outlined"
              />
            </Stack>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={newUser.email}
              onChange={handeAddUserChange}
              required
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={newUser.phone}
              onChange={handeAddUserChange}
              required
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>User Role</InputLabel>
              <Select
                name="role"
                value={newUser.role}
                label="User Role"
                onChange={handeAddUserChange}
              >
                <MenuItem value="user">Platform User</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Default password will be <strong>Password@123</strong>. Users can change it after verification.
            </Alert>
          </Stack>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={handleCloseAddDialog} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddUserSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {isSubmitting ? 'Creating...' : 'Create User'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Edit User Dialog */}
      <AdminDialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <AdminDialogHeader
          title="Edit User"
          subtitle="Update user contact information and account privileges"
          icon={Edit2}
          iconColor="primary"
          onClose={handleCloseEditDialog}
        />
        <AdminDialogContent dividers>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={editUser.first_name}
                onChange={handleEditUserChange}
                required
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={editUser.last_name}
                onChange={handleEditUserChange}
                required
                variant="outlined"
              />
            </Stack>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={editUser.email}
              onChange={handleEditUserChange}
              required
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={editUser.phone}
              onChange={handleEditUserChange}
              required
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>User Role</InputLabel>
              <Select
                name="role"
                value={editUser.role}
                label="User Role"
                onChange={handleEditUserChange}
              >
                <MenuItem value="user">Platform User</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={handleCloseEditDialog} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditUserSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {isSubmitting ? 'Updating...' : 'Update User'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Filter Sidebar Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={handleCloseFilter}
        title="Filter Users"
        subtitle="Narrow down customer accounts by role, status and verification"
        activeCount={[roleFilter, statusFilter, isVerifiedFilter].filter(Boolean).length}
        onReset={() => {
          setRoleFilter('');
          setStatusFilter('');
          setIsVerifiedFilter('');
          setPage(0);
        }}
        onApply={handleCloseFilter}
        isResetDisabled={!roleFilter && !statusFilter && !isVerifiedFilter}
      >
        {/* Role Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>USER ROLE</Typography>
          <FormControl fullWidth>
            <Select
              value={roleFilter}
              displayEmpty
              onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="user">Platform User</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Status Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>ACCOUNT STATUS</Typography>
          <FormControl fullWidth>
            <Select
              value={statusFilter}
              displayEmpty
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Activated">Activated</MenuItem>
              <MenuItem value="Blocked">Blocked</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Verification Filter */}
        <Box>
          <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: 1, mb: 1.5, display: 'block' }}>VERIFICATION</Typography>
          <FormControl fullWidth>
            <Select
              value={isVerifiedFilter}
              displayEmpty
              onChange={(e) => { setIsVerifiedFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2, bgcolor: 'background.neutral' }}
            >
              <MenuItem value="">All Verification Statuses</MenuItem>
              <MenuItem value="true">Verified Only</MenuItem>
              <MenuItem value="false">Unverified Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </FilterDrawer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: { width: 200 }
          }
        }}
      >
        {selectedUser?.auth_status === 'Blocked' ? (
          <MenuItem onClick={() => handleUpdateStatus('status', 'Activated')}>
            <Unlock size={18} style={{ marginRight: 12, color: theme.palette.success.main }} />
            <Typography variant="body2">Activate User</Typography>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleUpdateStatus('status', 'Blocked')}>
            <Lock size={18} style={{ marginRight: 12, color: theme.palette.error.main }} />
            <Typography variant="body2">Deactivate User</Typography>
          </MenuItem>
        )}

        {selectedUser?.isVerified ? (
          <MenuItem onClick={() => handleUpdateStatus('verified', false)}>
            <ShieldAlert size={18} style={{ marginRight: 12, color: theme.palette.warning.main }} />
            <Typography variant="body2">Unverify User</Typography>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleUpdateStatus('verified', true)}>
            <ShieldCheck size={18} style={{ marginRight: 12, color: theme.palette.success.main }} />
            <Typography variant="body2">Verify User</Typography>
          </MenuItem>
        )}

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        <MenuItem onClick={handleOpenEditDialog}>
          <Edit2 size={18} style={{ marginRight: 12, color: theme.palette.text.primary }} />
          <Typography variant="body2">Edit User</Typography>
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
          <Trash2 size={18} style={{ marginRight: 12 }} />
          <Typography variant="body2">Delete User</Typography>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDeleteUser}
        itemName={`${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim() || selectedUser?.email}
        itemType="user account"
        description={`Are you sure you want to delete ${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}? This will permanently remove their profile, listings, and associated data.`}
        confirmText="Delete User"
        loading={isSubmitting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersView;
